// F-role-gates · 2026-08-25 · Mapa canónico ruta → EmployeeAction.
//
// Este mapa es la fuente única de la verdad para el guard nuevo
// `<RequireAction>`. Se aplica sobre el `component:` del route config
// (via `withRequireAction`) para bloquear acceso por URL directa a
// secciones administrativas.
//
// Filosofía:
//   • Las rutas OPERATIVAS del TPV (carta camarero, checkout, product
//     picker) NO se gatean aquí — el flujo del supervisor override cubre
//     acciones concretas dentro de cada vista. Un camarero SIN permiso
//     concreto ve la vista, intenta la acción, sale el PIN dialog.
//   • Las rutas ADMINISTRATIVAS (gestión de datos maestros, informes,
//     configuración) SÍ se gatean — no tiene sentido enseñar la lista
//     de empleados a un cocinero, aunque no pueda modificar.
//
// Nombres de acciones: cualquier alias de `employeeActionAliases.js`
// (viejo ROLES_ENUM o nuevo EmployeeAction) — `RequireAction` los
// expande con `withLegacyAliases` para aceptar los dos.
//
// Convención: cada entry tiene `action` (string o array) y `label`
// (nombre humano de la sección para el NoPermissionView). Opcional:
// `mode:"all"` cuando hacen falta TODAS las acciones (default "any").

import { PATH_APP } from "./paths";

/** @typedef {{ action: string | string[], label: string, mode?: "any"|"all" }} RouteGate */

/** @type {Record<string, RouteGate>} */
const GATES = {
  // ── USUARIO / MI CUENTA ──────────────────────────────────────────
  // 2026-08-25 · Acciones dedicadas del grupo Account (backend).
  // `/user/account` acepta cualquiera de las 3 (mode any) — dentro de
  // la vista, cada tab se filtra individualmente con useCanDo.
  // 2026-08-27 · `/user` y `/user/account` comparten gate — cualquiera
  // de las 3 acciones del grupo Account. Antes `/user` exigía SOLO
  // PROFILE_MANAGE, así que un rol con solo Notificaciones o solo
  // Facturación caía a NoPermissionView aunque la pestaña se pintara
  // desde el nav.
  [PATH_APP.user.root]: {
    action: ["PROFILE_MANAGE", "ACCOUNT_NOTIFICATIONS_MANAGE", "BILLING_MANAGE"],
    mode: "any",
    label: "Mi cuenta",
  },
  [PATH_APP.user.account]: {
    action: ["PROFILE_MANAGE", "ACCOUNT_NOTIFICATIONS_MANAGE", "BILLING_MANAGE"],
    mode: "any",
    label: "Mi cuenta",
  },
  [PATH_APP.user.audit]: { action: "AUDIT_VIEW", label: "Auditoría" },
  [PATH_APP.user.tpvActivity]: { action: "AUDIT_TPV_VIEW", label: "Actividad del TPV" },
  [PATH_APP.user.employeeCorrections]: { action: "EMPLOYEE_APPROVE_ABSENCES", label: "Correcciones de empleados" },

  // ── TPV OPERATIVO ────────────────────────────────────────────────
  // La vista de mesas (hub del camarero) no se gatea — es el hogar del
  // camarero. Botones internos se gatean vía useCanDo/Can.
  [PATH_APP.tuCarta.tpv]: { action: "TPV_MANAGER", label: "TPV" },
  [PATH_APP.tuCarta.mesa]: { action: "TPV_MANAGER", label: "Mesa" },

  // ── CAJA ─────────────────────────────────────────────────────────
  [PATH_APP.tuCarta.caja]: { action: "ACCESS_CAJA", label: "Caja" },

  // ── EMPLEADOS ────────────────────────────────────────────────────
  [PATH_APP.tuCarta.employees]: { action: "RESTAURANT_EMPLOYEE_MANAGER", label: "Empleados" },
  [PATH_APP.tuCarta.createEmployee]: { action: "EMPLOYEE_MANAGE", label: "Crear empleado" },
  [PATH_APP.tuCarta.editEmployee]: { action: "EMPLOYEE_MANAGE", label: "Editar empleado" },
  [PATH_APP.tuCarta.createEmployeeRole]: { action: "EMPLOYEE_MANAGE", label: "Crear rol" },
  [PATH_APP.tuCarta.editEmployeeRole]: { action: "EMPLOYEE_MANAGE", label: "Editar rol" },
  [PATH_APP.tuCarta.employeeCalendar]: { action: "EMPLOYEE_VIEW", label: "Calendario de empleados" },
  [PATH_APP.tuCarta.employeeHolidays]: { action: "EMPLOYEE_VIEW", label: "Festivos" },

  // ── PANTALLAS DE COCINA (KDS) ────────────────────────────────────
  [PATH_APP.tuCarta.displays]: { action: "INVENTARY_DISPLAYS_MANAGER", label: "Pantallas de cocina" },
  [PATH_APP.tuCarta.displayAdd]: { action: "INVENTARY_DISPLAYS_MANAGER", label: "Crear pantalla" },
  [PATH_APP.tuCarta.displayEdit]: { action: "INVENTARY_DISPLAYS_MANAGER", label: "Editar pantalla" },

  // ── MI RESTAURANTE ───────────────────────────────────────────────
  [PATH_APP.tuCarta.mesas]: { action: "RESTAURANT_TABLE_MANAGER", label: "Mesas" },
  [PATH_APP.tuCarta.editTable]: { action: "TABLE_LAYOUT_MANAGE", label: "Editar mesa" },
  [PATH_APP.tuCarta.reservas]: { action: "RESTAURANT_BOOKING_MANAGER", label: "Reservas" },
  [PATH_APP.tuCarta.promociones]: { action: "LOYALTY_MANAGE", label: "Fidelización (sellos)" },
  [PATH_APP.tuCarta.pedidos]: { action: "ORDERS_MANAGE", label: "Pedidos" },
  [PATH_APP.tuCarta.discount]: { action: "RESTAURANT_DISCOUNT_MANAGER", label: "Descuentos" },
  [PATH_APP.tuCarta.editDiscount]: { action: "DISCOUNT_MANAGE", label: "Editar descuento" },
  [PATH_APP.tuCarta.addDiscount]: { action: "DISCOUNT_MANAGE", label: "Crear descuento" },
  // Redirige a Mesas ?tab=qr (Códigos QR); mismo permiso que antes.
  [PATH_APP.tuCarta.sessions]: { action: "RESTAURANT_SESSIONS_MANAGER", label: "Códigos QR de mesas" },
  [PATH_APP.tuCarta.paymentMethods]: { action: "RESTAURANT_PAYMENT_METHODS_MANAGER", label: "Métodos de pago" },
  [PATH_APP.tuCarta.paymentMethodAdd]: { action: "PAYMENT_METHOD_MANAGE", label: "Crear método de pago" },
  [PATH_APP.tuCarta.paymentMethodEdit]: { action: "PAYMENT_METHOD_MANAGE", label: "Editar método de pago" },

  // ── INVENTARIO ───────────────────────────────────────────────────
  [PATH_APP.tuCarta.inventario]: { action: "INVENTARY_GESTION_MANAGER", label: "Inventario y costes" },
  [PATH_APP.tuCarta.gestion]: { action: "INVENTARY_GESTION_MANAGER", label: "Dashboard de gestión" },
  [PATH_APP.tuCarta.gestionOperaciones]: { action: "INVENTARY_GESTION_MANAGER", label: "Operaciones de gestión" },
  [PATH_APP.tuCarta.gestionIngredienteAdd]: { action: "INVENTARY_GESTION_MANAGER", label: "Crear ingrediente" },
  [PATH_APP.tuCarta.gestionIngredienteEdit]: { action: "INVENTARY_GESTION_MANAGER", label: "Editar ingrediente" },
  [PATH_APP.tuCarta.gestionProveedorAdd]: { action: "INVENTARY_GESTION_MANAGER", label: "Crear proveedor" },
  [PATH_APP.tuCarta.gestionProveedorEdit]: { action: "INVENTARY_GESTION_MANAGER", label: "Editar proveedor" },
  [PATH_APP.tuCarta.gestionElaboracionAdd]: { action: "INVENTARY_GESTION_MANAGER", label: "Crear elaboración" },
  [PATH_APP.tuCarta.gestionElaboracionEdit]: { action: "INVENTARY_GESTION_MANAGER", label: "Editar elaboración" },
  [PATH_APP.tuCarta.gestionOrdenAdd]: { action: "INVENTARY_GESTION_MANAGER", label: "Crear orden de compra" },
  [PATH_APP.tuCarta.gestionAlbaranAdd]: { action: "INVENTARY_GESTION_MANAGER", label: "Crear albarán" },
  [PATH_APP.tuCarta.gestionRecetaEditor]: { action: "INVENTARY_GESTION_MANAGER", label: "Editor de recetas" },
  [PATH_APP.tuCarta.gestionEscandalloIA]: { action: "INVENTARY_GESTION_MANAGER", label: "Escandallo IA" },

  // "Productos" ruta = VER la carta. La gestión granular (crear/editar/
  // borrar) va gate en botones dentro de la vista con PRODUCT_CREATE /
  // PRODUCT_MODIFY / PRODUCT_DELETE.
  [PATH_APP.tuCarta.productos]: { action: "PRODUCT_VIEW", label: "Productos" },
  [PATH_APP.tuCarta.producto]: { action: "PRODUCT_MODIFY", label: "Editar producto" },
  [PATH_APP.tuCarta.newProducto]: { action: "PRODUCT_CREATE", label: "Crear producto" },
  [PATH_APP.tuCarta.productoComplemento]: { action: "PRODUCT_VIEW", label: "Producto" },

  [PATH_APP.tuCarta.categories]: { action: "INVENTARY_CATEGORIES_MANAGER", label: "Categorías" },
  [PATH_APP.tuCarta.addCategory]: { action: "CATEGORY_MANAGE", label: "Crear categoría" },
  [PATH_APP.tuCarta.editCategory]: { action: "CATEGORY_MANAGE", label: "Editar categoría" },
  [PATH_APP.tuCarta.orderCategory]: { action: "CATEGORY_MANAGE", label: "Ordenar categorías" },

  [PATH_APP.tuCarta.tags]: { action: "INVENTARY_TAGS_MANAGER", label: "Etiquetas" },
  [PATH_APP.tuCarta.addTag]: { action: "TAG_MANAGE", label: "Crear etiqueta" },
  [PATH_APP.tuCarta.editTag]: { action: "TAG_MANAGE", label: "Editar etiqueta" },
  [PATH_APP.tuCarta.orderTags]: { action: "TAG_MANAGE", label: "Ordenar etiquetas" },

  // Precios y promociones: Happy hour (reglas de precio por franja) +
  // Suplementos por zona (terraza…) + Informe. Rutas antiguas = redirección.
  [PATH_APP.tuCarta.precios]: { action: "PRICE_RULES_MANAGE", label: "Precios y promociones" },
  [PATH_APP.tuCarta.priceRules]: { action: "PRICE_RULES_MANAGE", label: "Precios y promociones" },
  [PATH_APP.tuCarta.zoneCharges]: { action: "PRICE_RULES_MANAGE", label: "Precios y promociones" },

  // ── FACTURACIÓN ──────────────────────────────────────────────────
  [PATH_APP.tuCarta.factura]: { action: "BILLING_CUSTOMERS_MANAGER", label: "Clientes" },
  [PATH_APP.tuCarta.facturaAdd]: { action: "INVOICE_ISSUE_CUSTOM", label: "Crear factura" },
  [PATH_APP.tuCarta.facturaEdit]: { action: "INVOICE_MODIFY", label: "Editar factura" },
  [PATH_APP.tuCarta.estadisticas]: { action: "BILLING_ANALYTICS_MANAGER", label: "Estadísticas" },
  [PATH_APP.tuCarta.estadisticasV2]: { action: "BILLING_ANALYTICS_MANAGER", label: "Estadísticas (nuevo)" },
  [PATH_APP.tuCarta.invoices]: { action: "BILLING_INVOICES_MANAGER", label: "Facturas" },

  // ── CONFIGURACIÓN ────────────────────────────────────────────────
  [PATH_APP.tuCarta.settings]: { action: "SETTINGS_ACCESS", label: "Ajustes" },
  [PATH_APP.tuCarta.printers]: { action: "RESTAURANT_PRINTER_MANAGER", label: "Impresoras" },
  [PATH_APP.tuCarta.addPrinter]: { action: "PRINTERS_MANAGE", label: "Crear impresora" },
  [PATH_APP.tuCarta.editPrinter]: { action: "PRINTERS_MANAGE", label: "Editar impresora" },
  [PATH_APP.tuCarta.integrations]: { action: "SETTINGS_STRIPE_MANAGER", label: "Integraciones" },
  [PATH_APP.tuCarta.integrationDetail]: { action: "SETTINGS_STRIPE_MANAGER", label: "Integración" },
  [PATH_APP.tuCarta.reviews]: { action: "REVIEWS_MANAGE", label: "Reseñas" },
  [PATH_APP.tuCarta.reviewDetail]: { action: "REVIEWS_MANAGE", label: "Reseña" },
  [PATH_APP.tuCarta.reportsDaily]: { action: "SETTINGS_MODIFY", label: "Informes diarios" },
  [PATH_APP.tuCarta.monitor]: { action: "MONITOR_ACCESS", label: "Insights Monitor" },
  [PATH_APP.tuCarta.logs]: { action: "AUDIT_VIEW", label: "Actividad" },
  [PATH_APP.user.aeatAudit]: { action: "AEAT_ACCESS", label: "Auditoría Hacienda" },
  [PATH_APP.user.aeatErrors]: { action: "AEAT_ACCESS", label: "Auditoría Hacienda" },
};

/**
 * Devuelve la puerta configurada para una ruta absoluta.
 * @param {string} routePath - ej. "/app/tucarta/employees"
 */
export function getRouteGate(routePath) {
  return GATES[routePath] || null;
}

export default GATES;
